import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Download, Upload, Image as ImageIcon, Video, Search } from 'lucide-react';
import contentService from '../services/contentService';
import { API_BASE_URL } from '../config/api';

const Content = () => {
  const { contentSlug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);

  useEffect(() => {
    if (!showMediaModal) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMediaModal(false);
        setSelectedMedia(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMediaModal]);
  
  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [contentName, setContentName] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedAudio, setSelectedAudio] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const sortedContent = useMemo(() => {
    if (!Array.isArray(content)) {
      return [];
    }
    return [...content].sort((a, b) => {
      const aDate = new Date(a.postedAt || a.createdAt || 0).getTime();
      const bDate = new Date(b.postedAt || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [content]);

  const formatDisplayDate = (value) => {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    if (contentSlug && content.length > 0) {
      let item = (content || []).find(c => c.id === contentSlug);
      if (!item) {
        item = (content || []).find(c => {
          const slug = c.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          return slug === contentSlug;
        });
      }
      if (item) {
        loadSpecificContent(item.folder || item.id);
      } else {
        loadSpecificContent(contentSlug);
      }
    }
  }, [contentSlug, content]);

  const loadSpecificContent = async (contentId) => {
    try {
      const item = await contentService.getContentItem(contentId);
      if (item) {
        setSelectedContent({
          ...item,
          postedAt: item.postedAt || item.createdAt || item.updatedAt || null
        });
      }
    } catch (error) {
      console.error('Error loading specific content:', error);
    }
  };

  const loadContent = async () => {
    try {
      setLoading(true);
      const fetchedContent = await contentService.getContent();
      const normalized = Array.isArray(fetchedContent)
        ? fetchedContent.map(item => ({
            ...item,
            postedAt: item.postedAt || item.createdAt || item.updatedAt || null
          }))
        : [];
      setContent(normalized);
      if (normalized.length > 0) {
        contentService.saveContentToLocalStorage(normalized);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      const fallbackContent = contentService.getContentFromLocalStorage();
      const normalizedFallback = Array.isArray(fallbackContent)
        ? fallbackContent.map(item => ({
            ...item,
            postedAt: item.postedAt || item.createdAt || item.updatedAt || null
          }))
        : [];
      setContent(normalizedFallback);
    } finally {
      setLoading(false);
    }
  };

  const downloadMedia = (media) => {
    const link = document.createElement('a');
    link.href = media.url;
    link.download = media.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openMediaModal = (media) => {
    const fallbackCaption = media.type === 'video'
      ? `${media.filename} • Zee motion`
      : `${media.filename} • Zee close-up`;
    setSelectedMedia({
      ...media,
      caption: media.caption || fallbackCaption
    });
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
    setShowMediaModal(false);
  };

  const handleContentUpload = async () => {
    if (!contentName || (selectedImages.length === 0 && selectedVideos.length === 0 && selectedAudio.length === 0)) {
      alert('Please provide a name and at least one media file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);

    try {
      const formData = new FormData();
      
      // Backend expects: title, description, contentType, coverImage, image, video, audio
      formData.append('title', contentName);
      if (contentDescription) formData.append('description', contentDescription);
      formData.append('contentType', 'blog'); // Default to blog for website content
      formData.append('isActive', 'true'); // Publish immediately
      formData.append('isPublic', 'true'); // Make it public
      
      // Cover image (use first image if no cover selected)
      if (coverImage) {
        formData.append('coverImage', coverImage);
      } else if (selectedImages.length > 0) {
        formData.append('coverImage', selectedImages[0]);
      }
      
      // First image (backend expects 'image' field)
      if (selectedImages.length > 0) {
        formData.append('image', selectedImages[0]);
      }
      
      // First video (backend expects 'video' field)
      if (selectedVideos.length > 0) {
        formData.append('video', selectedVideos[0]);
      }

      // First audio file
      if (selectedAudio.length > 0) {
        formData.append('audio', selectedAudio[0]);
      }

      // Use the unified backend API for content upload
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please log in to upload content');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Content uploaded successfully:', result);
        
        // Clear cache to force refresh
        contentService.clearCache();
        
        // Reload content list
        await loadContent();
        
        // Reset form
        setContentName('');
        setContentDescription('');
        setCoverImage(null);
        setSelectedImages([]);
        setSelectedVideos([]);
        setSelectedAudio([]);
        setShowUploadModal(false);
        
        setTimeout(() => {
          setUploadProgress(0);
        }, 2000);
      } else {
        const error = await response.json();
        console.error('❌ Upload failed:', error);
        alert('Upload failed: ' + (error.error || 'Unknown error'));
        clearInterval(progressInterval);
      }
    } catch (error) {
      console.error('❌ Content upload error:', error);
      alert('Upload failed: ' + error.message);
      clearInterval(progressInterval);
    } finally {
      setUploading(false);
    }
  };

  const filteredContent = (sortedContent || []).filter(item =>
    !searchQuery ||
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  // Individual Content View
  if (contentSlug && selectedContent) {
    let allMedia = [];
    const seenUrls = new Set();
    
    if (selectedContent.images && Array.isArray(selectedContent.images)) {
      selectedContent.images.forEach((url, index) => {
        if (!seenUrls.has(url)) {
          allMedia.push({
            type: 'image',
            id: `img-${index}`,
            url: url.startsWith('http') ? url : `${window.location.origin}${url}`,
            filename: url.split('/').pop() || `image-${index + 1}.jpg`,
            caption: `${selectedContent.name} • Zee image ${index + 1}`
          });
          seenUrls.add(url);
        }
      });
    }
    
    if (selectedContent.videos && Array.isArray(selectedContent.videos)) {
      selectedContent.videos.forEach((url, index) => {
        if (!seenUrls.has(url)) {
          allMedia.push({
            type: 'video',
            id: `video-${index}`,
            url: url.startsWith('http') ? url : `${window.location.origin}${url}`,
            filename: url.split('/').pop() || `video-${index + 1}.mp4`,
            caption: `${selectedContent.name} • Zee video ${index + 1}`
          });
          seenUrls.add(url);
        }
      });
    }

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
        padding: window.innerWidth <= 768 ? '20px 16px' : '20px',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '80px auto 40px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: window.innerWidth <= 768 ? '20px 16px' : '30px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: 'white',
            margin: '0 0 10px 0',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}>
            {selectedContent.name}
          </h1>
          {selectedContent.description && (
            <p style={{ color: '#e5e7eb', fontSize: '18px', marginTop: '10px' }}>
              {selectedContent.description}
            </p>
          )}
          {formatDisplayDate(selectedContent.postedAt || selectedContent.createdAt) && (
            <p style={{ color: '#d1d5db', fontSize: '14px', marginTop: '10px', letterSpacing: '0.5px' }}>
              Posted on{' '}
              {formatDisplayDate(selectedContent.postedAt || selectedContent.createdAt)}
            </p>
          )}
        </div>

        {allMedia.length > 0 && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: window.innerWidth <= 768 ? '20px 16px' : '30px',
            width: '100%',
            boxSizing: 'border-box',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '30px',
              textAlign: 'center',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}>
              Media Gallery
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {allMedia.map((media) => (
                <div
                  key={media.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`${media.type === 'video' ? 'Play' : 'View'} ${media.filename} in full screen`}
                  onClick={() => openMediaModal(media)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(236, 72, 153, 0.3)';
                    if (media.type === 'video') {
                      const video = e.currentTarget.querySelector('video');
                      if (video) video.play();
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    if (media.type === 'video') {
                      const video = e.currentTarget.querySelector('video');
                      if (video) {
                        video.pause();
                        video.currentTime = 0;
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openMediaModal(media);
                    }
                  }}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'zoom-in',
                    position: 'relative',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(236, 72, 153, 0.6)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {media.type === 'image' ? (
                    <img
                      src={media.url}
                      alt={media.filename}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '250px',
                        objectFit: 'cover',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ) : (
                    <>
                      <video
                        src={media.url}
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover'
                        }}
                        muted
                        loop
                        playsInline
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(236, 72, 153, 0.8)',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}>
                        <Play size={20} color="white" />
                      </div>
                    </>
                  )}
                  
                  <div style={{
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: 'white', fontSize: '14px' }}>
                      {media.type === 'video' ? '🎬' : '📸'} {media.filename}
                    </span>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadMedia(media);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media Modal */}
        {showMediaModal && selectedMedia && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Zee full screen media viewer"
            onClick={closeMediaModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(10, 10, 20, 0.92)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '32px 16px',
              zIndex: 1500,
              backdropFilter: 'blur(8px)'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '96vw',
                maxHeight: '88vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}
            >
              <button
                onClick={closeMediaModal}
                style={{
                  alignSelf: 'flex-end',
                  background: 'rgba(255, 255, 255, 0.12)',
                  color: '#f472b6',
                  border: '1px solid rgba(244, 114, 182, 0.4)',
                  borderRadius: '9999px',
                  padding: '8px 18px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = 'rgba(244, 114, 182, 0.25)';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                }}
              >
                Zee Close
              </button>

              {selectedMedia.type === 'image' ? (
                <img
                  src={selectedMedia.url}
                  alt={selectedMedia.caption || selectedMedia.filename}
                  style={{
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '16px',
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.6)',
                    cursor: 'zoom-out'
                  }}
                />
              ) : (
                <video
                  src={selectedMedia.url}
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '16px',
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.6)'
                  }}
                />
              )}

              {selectedMedia.caption && (
                <p style={{ color: '#e5e7eb', fontSize: '16px', textAlign: 'center' }}>
                  {selectedMedia.caption}
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    downloadMedia(selectedMedia);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '10px 20px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.transform = 'translateY(-2px)';
                    event.currentTarget.style.boxShadow = '0 10px 25px rgba(34, 197, 94, 0.35)';
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.transform = 'translateY(0)';
                    event.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Download size={16} />
                  Zee Download
                </button>
                <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', margin: 0 }}>
                  Zee tip: pinch, spread, or scroll to zoom; tap outside to close.
                </p>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Content List View (default)
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
      padding: '40px 20px'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '40px auto 40px auto',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <h1 style={{
          fontSize: '64px',
          fontWeight: 'bold',
          color: 'white',
          margin: '0 0 20px 0',
          textShadow: '3px 3px 6px rgba(0,0,0,0.5)',
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Content
        </h1>
        
        <p style={{
          fontSize: '20px',
          color: '#e5e7eb',
          marginBottom: '40px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
        }}>
          Browse and download images and videos
        </p>

        {/* Upload Button */}
        <button
          onClick={() => setShowUploadModal(true)}
          style={{
            padding: '15px 30px',
            borderRadius: '50px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            background: 'rgba(34, 197, 94, 0.2)',
            color: '#22c55e',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '30px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(34, 197, 94, 0.3)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Upload size={20} />
          Upload Content
        </button>

        {/* Search Bar */}
        <div style={{
          maxWidth: '500px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 10
        }}>
          <div style={{
            position: 'absolute',
            left: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }}>
            <Search size={20} />
          </div>
          
          <input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '15px 15px 15px 50px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              color: 'white',
              fontSize: '16px',
              outline: 'none',
              transition: 'all 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Content Grid */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        {filteredContent.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '60px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '10px' }}>
              No content available yet
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '16px' }}>
              Upload your first content using the button above!
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {filteredContent.map(item => (
              <div
                key={item.id}
                onClick={() => {
                  setSelectedContent(item);
                  const slug = item.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                  navigate(`/content/${slug}`);
                }}
                style={{
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(251, 191, 36, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                }}
              >
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {item.coverImage ? (
                    <img
                      src={item.coverImage.startsWith('http') ? item.coverImage : `${window.location.origin}${item.coverImage}`}
                      alt={item.name}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '200px',
                        background: 'linear-gradient(135deg, #374151, #1f2937)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        fontSize: '16px'
                      }}
                    >
                      📸 Content
                    </div>
                  )}
                </div>

                <div style={{ padding: '20px' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {item.name}
                  </h3>
                  
                  {formatDisplayDate(item.postedAt || item.createdAt) && (
                    <div style={{ color: '#fbbf24', fontSize: '0.85rem', marginBottom: '12px', fontWeight: 500 }}>
                      Posted {formatDisplayDate(item.postedAt || item.createdAt)}
                    </div>
                  )}
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#fbbf24' }}>
                      <ImageIcon size={14} />
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {item.images?.length || 0} Images
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#fbbf24' }}>
                      <Video size={14} />
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {item.videos?.length || 0} Videos
                      </span>
                    </div>
                  </div>
                  
                  {item.description && (
                    <p style={{
                      color: '#9ca3af',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      margin: 0
                    }}>
                      {item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          onClick={() => !uploading && setShowUploadModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(31, 41, 55, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '20px', textAlign: 'center' }}>
              Upload Content
            </h2>

            <input
              type="text"
              placeholder="Content Name *"
              value={contentName}
              onChange={(e) => setContentName(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '15px',
                outline: 'none'
              }}
            />

            <textarea
              placeholder="Description (optional)"
              value={contentDescription}
              onChange={(e) => setContentDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '15px',
                outline: 'none',
                resize: 'vertical'
              }}
            />

            <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Cover Image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCoverImage(e.target.files[0])}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '15px'
              }}
            />

            <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Images *
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setSelectedImages(Array.from(e.target.files))}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '15px'
              }}
            />

            <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Videos
            </label>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => setSelectedVideos(Array.from(e.target.files))}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '20px'
              }}
            />

            <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Audio
            </label>
            <input
              type="file"
              accept="audio/*"
              multiple
              onChange={(e) => setSelectedAudio(Array.from(e.target.files))}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                marginBottom: '20px'
              }}
            />

            {uploading && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center' }}>
                  {Math.round(uploadProgress)}% complete
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleContentUpload}
                disabled={uploading || !contentName}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: uploading || !contentName ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: uploading || !contentName ? 'not-allowed' : 'pointer',
                  opacity: uploading || !contentName ? 0.6 : 1
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  background: 'transparent',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Content;

